import { getLotReferenceLabel } from './lotsUtils';

export const REFERENCE_STATE_CONFIG = [
  { label: 'Constructora Colonial - Obra en progreso', color: '#ff9800' },
  { label: 'No disponible', color: '#9e9e9e' },
  { label: 'Obra completada', color: '#d32f2f' },
  { label: 'Asignado a Von Der Heyde', color: '#1b1b1b' },
  { label: 'Asignado a Constructora Colonial', color: '#f9a825' },
  { label: 'Disponible', color: '#2e7d32' },
  { label: 'Boleto asociado', color: '#4fc3f7' },
  { label: 'Asignado a un contrato', color: '#1565c0' },
  { label: 'Obra en proceso', color: '#1e88e5' },
];

const REFERENCE_STATE_COLORS = REFERENCE_STATE_CONFIG.reduce((acc, item) => {
  acc[item.label] = item.color;
  return acc;
}, {});

const toDate = (value) => {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const monthKeyFromDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const getMonthLabel = (date) =>
  date.toLocaleDateString('es-AR', {
    month: 'short',
    year: '2-digit',
  });

const getProjectForLot = (lot, projectsMap = {}) => lot?.project || projectsMap?.[lot?.uuid] || null;

const getLotProgress = (lot) => Number(lot?.project_progress_summary?.completion_percentage ?? 0);
const getLotQuality = (lot) => Number(lot?.project_quality_summary?.completion_percentage ?? 0);
const getLotIdentifier = (lot) => `${lot?.block_number || '-'} - ${lot?.lot_number || '-'}`;

const getReferenceState = (lot, projectsMap = {}) =>
  getLotReferenceLabel({
    lot,
    project: getProjectForLot(lot, projectsMap),
  });

const buildMonthlyWindow = () => {
  const base = new Date();
  base.setHours(0, 0, 0, 0);
  const currentMonth = new Date(base.getFullYear(), base.getMonth(), 1);
  const windowStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 5, 1);
  const months = [];

  for (let index = 0; index < 12; index += 1) {
    const month = new Date(windowStart.getFullYear(), windowStart.getMonth() + index, 1);
    months.push({
      key: monthKeyFromDate(month),
      month: getMonthLabel(month),
      programadas: 0,
      completadas: 0,
    });
  }

  return months;
};

const abbreviate = (value, maxLength = 18) => {
  const text = String(value || '').trim();
  if (!text) return 'Sin subcontratista';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1)}…`;
};

export const buildLotsDashboardData = (lots = [], projectsMap = {}) => {
  const stateCounter = new Map(REFERENCE_STATE_CONFIG.map((item) => [item.label, 0]));
  const constructionTypeMap = new Map();
  const monthData = buildMonthlyWindow();
  const monthIndex = new Map(monthData.map((item, index) => [item.key, index]));
  const scatterData = [];
  const subcontractorMap = new Map();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  lots.forEach((lot) => {
    const project = getProjectForLot(lot, projectsMap);
    const state = getReferenceState(lot, projectsMap);
    stateCounter.set(state, (stateCounter.get(state) || 0) + 1);

    const type = (lot?.construction_type_name || '').trim() || 'Sin tipo';
    const hasContract = Boolean(lot?.uuid_contract || lot?.contract?.uuid || lot?.contract?.contract_number);
    if (!constructionTypeMap.has(type)) {
      constructionTypeMap.set(type, { tipo: type, conContrato: 0, sinContrato: 0, total: 0 });
    }
    const typeBucket = constructionTypeMap.get(type);
    typeBucket.total += 1;
    if (hasContract) {
      typeBucket.conContrato += 1;
    } else {
      typeBucket.sinContrato += 1;
    }

    const estimatedDate = toDate(lot?.estimated_delivery_date);
    if (estimatedDate) {
      const key = monthKeyFromDate(new Date(estimatedDate.getFullYear(), estimatedDate.getMonth(), 1));
      const monthPos = monthIndex.get(key);
      if (monthPos !== undefined) {
        monthData[monthPos].programadas += 1;
        if (state === 'Obra completada') {
          monthData[monthPos].completadas += 1;
        }
      }
    }

    const progress = getLotProgress(lot);
    const quality = getLotQuality(lot);
    if (project?.uuid || progress > 0 || quality > 0) {
      scatterData.push({
        id: getLotIdentifier(lot),
        progreso: progress,
        calidad: quality,
        estado: state,
        subcontratista: project?.subcontractor_name || 'Sin subcontratista',
      });
    }

    const subcontractorName = project?.subcontractor_name || 'Sin subcontratista';
    if (!subcontractorMap.has(subcontractorName)) {
      subcontractorMap.set(subcontractorName, {
        fullName: subcontractorName,
        lots: 0,
        progressSum: 0,
        qualitySum: 0,
        datedLots: 0,
        onTimeLots: 0,
      });
    }
    const subcontractor = subcontractorMap.get(subcontractorName);
    subcontractor.lots += 1;
    subcontractor.progressSum += progress;
    subcontractor.qualitySum += quality;

    if (estimatedDate) {
      subcontractor.datedLots += 1;
      const isCompleted = state === 'Obra completada';
      const onTime = isCompleted || estimatedDate.getTime() >= today.getTime();
      if (onTime) {
        subcontractor.onTimeLots += 1;
      }
    }
  });

  const stateDistributionData = REFERENCE_STATE_CONFIG.map((item) => ({
    name: item.label,
    value: stateCounter.get(item.label) || 0,
    fill: item.color,
  })).filter((item) => item.value > 0);

  const totalLots = lots.length;
  const pieData = stateDistributionData.map((item) => ({
    ...item,
    percentage: totalLots > 0 ? Number(((item.value / totalLots) * 100).toFixed(1)) : 0,
  }));

  const constructionContractMixData = Array.from(constructionTypeMap.values())
    .sort((a, b) => b.total - a.total)
    .slice(0, 8);

  const deliveryTimelineData = monthData;

  const subcontractorPerformanceData = Array.from(subcontractorMap.values())
    .sort((a, b) => b.lots - a.lots)
    .slice(0, 6)
    .map((item) => ({
      subcontractor: abbreviate(item.fullName),
      fullName: item.fullName,
      lotes: item.lots,
      progreso: Number((item.progressSum / item.lots).toFixed(1)),
      calidad: Number((item.qualitySum / item.lots).toFixed(1)),
      cumplimiento:
        item.datedLots > 0 ? Number(((item.onTimeLots / item.datedLots) * 100).toFixed(1)) : 100,
    }));

  return {
    totals: {
      lots: totalLots,
      withContract: lots.filter((lot) => Boolean(lot?.uuid_contract || lot?.contract?.uuid)).length,
      withEstimatedDate: lots.filter((lot) => Boolean(toDate(lot?.estimated_delivery_date))).length,
      completedState: lots.filter(
        (lot) => getReferenceState(lot, projectsMap) === 'Obra completada'
      ).length,
    },
    chartData: {
      stateDistributionData: pieData,
      constructionContractMixData,
      deliveryTimelineData,
      progressQualityScatterData: scatterData,
      subcontractorPerformanceData,
    },
    colors: REFERENCE_STATE_COLORS,
  };
};

